import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Row, Col } from 'react-bootstrap';

import { Ico94, Ico95, Ico96, Ico98 } from '@icons/new';
import { getEsgCategories } from '@lib/api/index';
import RadarChart from 'components/project/radarChart/RadarChart';
import RadarCard from 'components/project/RadarCard';

const ESGRadarCardsComponent = ({ project, compareProject, translations }) => {
  const [environmentalSubcategories, setEnvironmentalSubcategories] = useState([]);
  const [socialSubcategories, setSocialSubcategories] = useState([]);
  const [governanceSubcategories, setGovernanceSubcategories] = useState([]);

  const [dataChartEnvironment, setDataChartEnvironment] = useState([]);
  const [dataChartSocial, setDataChartSocial] = useState([]);
  const [dataChartGovernance, setDataChartGovernance] = useState([]);

  const [compareDataChartEnvironment, setCompareDataChartEnvironment] = useState([]);
  const [compareDataChartSocial, setCompareDataChartSocial] = useState([]);
  const [compareDataChartGovernance, setCompareDataChartGovernance] = useState([]);

  const [radarDivWidth, setRadarDivWidth] = useState(0);

  // ✅ ref al container del primo radar (quello E)
  const radarMeasureRef = useRef(null);

  // ✅ ResizeObserver stabile + cleanup
  useEffect(() => {
    if (!radarMeasureRef.current) return;

    const ro = new ResizeObserver((entries) => {
      const w = entries?.[0]?.contentRect?.width ?? 0;
      setRadarDivWidth(w);
    });

    ro.observe(radarMeasureRef.current);

    return () => {
      ro.disconnect();
    };
  }, []);

  // categorie
  useEffect(() => {
    let cancelled = false;

    getEsgCategories().then((retrievedCategories) => {
      if (cancelled) return;

      setEnvironmentalSubcategories(
        retrievedCategories.filter((cat) => cat.code === 'E').flatMap((c) => c.esgsubcategories),
      );
      setSocialSubcategories(
        retrievedCategories.filter((cat) => cat.code === 'S').flatMap((c) => c.esgsubcategories),
      );
      setGovernanceSubcategories(
        retrievedCategories.filter((cat) => cat.code === 'G').flatMap((c) => c.esgsubcategories),
      );
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // dati radar
  useEffect(() => {
    if (!project?.esgratings?.length) return;

    setDataChartEnvironment(
      project.esgratings
        .filter((r) => r.category === 'E' && r.type === 'subcategory')
        .sort((a, b) => a.scorePerc - b.scorePerc)
        .map((r) => ({
          axis: environmentalSubcategories
            .filter((sub) => sub.code === r.subcategory)
            .map((sub) => sub.text)
            .join(),
          value: r.scorePerc,
        })),
    );

    setDataChartSocial(
      project.esgratings
        .filter((r) => r.category === 'S' && r.type === 'subcategory')
        .sort((a, b) => a.scorePerc - b.scorePerc)
        .map((r) => ({
          axis: socialSubcategories
            .filter((sub) => sub.code === r.subcategory)
            .map((sub) => sub.text)
            .join(),
          value: r.scorePerc,
        })),
    );

    setDataChartGovernance(
      project.esgratings
        .filter((r) => r.category === 'G' && r.type === 'subcategory')
        .sort((a, b) => a.scorePerc - b.scorePerc)
        .map((r) => ({
          axis: governanceSubcategories
            .filter((sub) => sub.code === r.subcategory)
            .map((sub) => sub.text)
            .join(),
          value: r.scorePerc,
        })),
    );
  }, [project, environmentalSubcategories, socialSubcategories, governanceSubcategories]);
  useEffect(() => {
    if (!compareProject?.esgratings?.length) return;

    setCompareDataChartEnvironment(
      compareProject.esgratings
        .filter((r) => r.category === 'E' && r.type === 'subcategory')
        .sort((a, b) => a.scorePerc - b.scorePerc)
        .map((r) => ({
          axis: environmentalSubcategories
            .filter((sub) => sub.code === r.subcategory)
            .map((sub) => sub.text)
            .join(),
          value: r.scorePerc,
        })),
    );

    setCompareDataChartSocial(
      compareProject.esgratings
        .filter((r) => r.category === 'S' && r.type === 'subcategory')
        .sort((a, b) => a.scorePerc - b.scorePerc)
        .map((r) => ({
          axis: socialSubcategories
            .filter((sub) => sub.code === r.subcategory)
            .map((sub) => sub.text)
            .join(),
          value: r.scorePerc,
        })),
    );

    setCompareDataChartGovernance(
      compareProject.esgratings
        .filter((r) => r.category === 'G' && r.type === 'subcategory')
        .sort((a, b) => a.scorePerc - b.scorePerc)
        .map((r) => ({
          axis: governanceSubcategories
            .filter((sub) => sub.code === r.subcategory)
            .map((sub) => sub.text)
            .join(),
          value: r.scorePerc,
        })),
    );
  }, [compareProject, environmentalSubcategories, socialSubcategories, governanceSubcategories]);

  const functionDrawRadar = (data, radarColor, divWidth, maxValue, compareData) => {
    return (
      <RadarChart
        data={[data]}
        compareData={compareData ? [compareData] : undefined}
        radarColor={radarColor}
        divWidth={divWidth}
        maxValue={maxValue ?? 100}
      />
    );
  };

  const radarCards = useMemo(
    () => [
      {
        key: 'E',
        cardId: 'div-radar-1',
        radarRowId: 'radar-1',
        Icon: Ico94,
        iconClassName: 'logo-card ico-box-green',
        titleCode: 'ICS039',
        tooltipPlacement: 'right',
        data: dataChartEnvironment,
        compareData: compareDataChartEnvironment, // opzionale
        radarColor: '#c2c923',
        maxValue: 100,
        // ✅ solo la prima card riceve la ref per la misura
        containerRef: radarMeasureRef,
      },
      {
        key: 'S',
        cardId: 'div-radar-2',
        radarRowId: 'radar-2',
        Icon: Ico95,
        iconClassName: 'logo-card ico-box-light-blue',
        titleCode: 'ICS040',
        tooltipPlacement: 'right',
        data: dataChartSocial,
        compareData: compareDataChartSocial,
        radarColor: '#08a2aa',
        maxValue: 100,
      },
      {
        key: 'G',
        cardId: 'div-radar-3',
        radarRowId: 'radar-3',
        Icon: Ico96,
        iconClassName: 'logo-card ico-box-blue',
        titleCode: 'ICS048',
        tooltipPlacement: 'left',
        data: dataChartGovernance,
        compareData: compareDataChartGovernance,
        radarColor: '#1c2743',
        maxValue: 100,
      },
    ],
    [
      dataChartEnvironment,
      dataChartSocial,
      dataChartGovernance,
      compareDataChartEnvironment,
      compareDataChartSocial,
      compareDataChartGovernance,
    ],
  );

  return (
    <Row className='gap-1 mb-5' style={{ marginTop: '1.8rem' }}>
      {radarCards.map((cfg) => (
        <Col key={cfg.key}>
          <RadarCard
            cardId={cfg.cardId}
            radarRowId={cfg.radarRowId}
            containerRef={cfg.containerRef} // ✅ ref solo su E
            Icon={cfg.Icon}
            iconClassName={cfg.iconClassName}
            titleCode={cfg.titleCode}
            tooltipPlacement={cfg.tooltipPlacement}
            translations={translations}
            data={cfg.data}
            compareData={cfg.compareData}
            radarDivWidth={radarDivWidth}
            radarColor={cfg.radarColor}
            maxValue={cfg.maxValue}
            renderRadar={functionDrawRadar}
          />
        </Col>
      ))}
    </Row>
  );
};

export default ESGRadarCardsComponent;
